import React, { Fragment, ReactElement } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

export interface ICommandBarMenuItem {
    itemKey?: string;
    text?: string | (() => string);
    onClick?: () => void;
    iconOnly?: boolean;
    icon?: ReactElement;
    subMenuProps?: ICommandBarMenu;
    className?: string | (() => string);
    title?: string;
}

interface ICommandBarMenu {
    items?: ICommandBarMenuItem[];
}

interface ICommandBar {
    className?: string;
    items: ICommandBarMenuItem[];
    farItems?: ICommandBarMenuItem[];
}

const ToolbarButtonItem = ({ itemKey, text, onClick, iconOnly, icon, className, title }: ICommandBarMenuItem): ReactElement => {
    const textContent = typeof text === "function" ? text() : text;
    const classNames = className && typeof className === "function" ? className() : className;
    const extraClass = classNames ? ` ${classNames}` : "";
    const surfaceClass = `inline-flex items-center px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors mr-1${extraClass}`;
    const selectableSurfaceStyle: React.CSSProperties = { userSelect: "text" };
    const content = (
        <>
            {icon && <span className="mr-1">{icon}</span>}
            {iconOnly ? null : textContent}
        </>
    );

    // Buttons default to user-select: none in browsers; use a span for read-only labels so sums and counts can be copied.
    if (!onClick) {
        return (
            <span className={surfaceClass} style={selectableSurfaceStyle} title={title} role="status">
                {content}
            </span>
        );
    }

    return (
        <button type="button" className={surfaceClass} style={selectableSurfaceStyle} title={title} onClick={() => onClick()}>
            {content}
        </button>
    );
};

const ToolbarMenu = ({ itemKey, text, subMenuProps, className, title }: ICommandBarMenuItem): ReactElement => {
    const textContent = typeof text === "function" ? text() : text;
    const classNames = className && typeof className === "function" ? className() : className;
    return (
        <DropdownMenu.Root key={itemKey}>
            <DropdownMenu.Trigger asChild>
                <button
                    className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors mr-1 ${classNames}`}
                    title={title}
                >
                    {textContent}
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content className="min-w-[220px] bg-white rounded-md p-1 shadow-md border border-gray-200 z-50">
                    {subMenuProps && subMenuProps.items && subMenuProps.items.length > 0
                        ? subMenuProps.items.map((item: ICommandBarMenuItem) => (
                              <DropdownMenu.Item
                                  key={item.itemKey}
                                  className="flex items-center px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-pointer"
                                  onSelect={item.onClick}
                              >
                                  {item.icon && <span className="mr-2">{item.icon}</span>}
                                  {typeof item.text === "function" ? item.text() : item.text}
                              </DropdownMenu.Item>
                          ))
                        : null}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};

export const CommandBar = ({ items, farItems, className }: ICommandBar): ReactElement => {
    const renderToolbarItem = (item): ReactElement => {
        return item.subMenuProps ? <ToolbarMenu key={item.itemKey} {...item} /> : <ToolbarButtonItem key={item.itemKey} {...item} />;
    };

    return (
        <Fragment>
            <div className={`flex justify-between items-center p-2 border-b ${className || ""}`}>
                <div className="flex gap-1">
                    {items && items.length > 0 ? items.map((item: ICommandBarMenuItem) => renderToolbarItem(item)) : null}
                </div>
                <div className="flex gap-1">
                    {farItems && farItems.length > 0 ? farItems.map((item: ICommandBarMenuItem) => renderToolbarItem(item)) : null}
                </div>
            </div>
        </Fragment>
    );
};
